import React, { useMemo } from 'react';
import CompanySearch from './CompanySearch';
import LoadingSpinner from './common/LoadingSpinner';
import ErrorMessage from './common/ErrorMessage';
import { apiGet } from '../utils/api';
import { useDataFetch } from '../hooks/useApi';
import { useUrlState } from '../hooks/useUrlState';
import ExcelJS from 'exceljs';
import './HealthInsuranceSettlement.css';

/**
 * 건강보험연말정산 — 회사 선택만으로 해당 연도 임금대장(상용직)에서
 * 직원별 월별 건강보험·요양보험 공제액을 취합해 표시/엑셀 다운로드.
 * (기존 엑셀 전수 순회 매크로 sol1의 DB 기반 대체)
 * 그 해에 퇴직한 직원은 퇴직할 때 따로 정산하므로 결과에 넣지 않고 화면 아래에 명단만 보여 준다.
 */
function HealthInsuranceSettlement() {
  // 고른 기업·연도는 주소에 둔다(?company=…&year=…) — 주소를 보내면 같은 화면이 열린다
  const [url, setUrl] = useUrlState();
  const year = Number(url.get('year')) || new Date().getFullYear();
  const setYear = (y) => setUrl({ year: y });

  const { data: companies, loading: companiesLoading, error: companiesError } =
    useDataFetch(() => apiGet('/api/companies/'), []);
  const companyId = url.get('company');
  const selectedCompany = useMemo(
    () => (companies || []).find((c) => String(c.id) === String(companyId)) || null,
    [companies, companyId],
  );
  const setSelectedCompany = (c) => setUrl({ company: c ? c.id : null });
  const { data: users } = useDataFetch(() => apiGet('/api/companies/users/list'), []);

  const { data: settlement, loading: settlementLoading, error: settlementError } =
    useDataFetch(
      () => selectedCompany
        ? apiGet(`/api/employees/health-insurance/${selectedCompany.id}?year=${year}`)
        : null,
      [selectedCompany, year]
    );

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const months = settlement?.available_months || [];
  const employees = settlement?.employees || [];
  const retired = settlement?.retired || [];

  const fmt = (n) => (n ? Math.round(n).toLocaleString() : '-');

  const monthTotal = (month, field) =>
    employees.reduce((s, emp) => s + (emp.monthly?.[String(month)]?.[field] || 0), 0);

  const handleExcelDownload = async () => {
    if (!settlement || employees.length === 0) {
      alert('다운로드할 데이터가 없습니다.');
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet(`${year}년 건강보험연말정산`);

    ws.columns = [
      { header: '성명', key: 'name', width: 12 },
      { header: '주민번호', key: 'id_number', width: 16 },
      { header: '입사일', key: 'hire_date', width: 12 },
      ...months.flatMap(m => [
        { header: `${m}_건강`, key: `${m}_h`, width: 11 },
        { header: `${m}_요양`, key: `${m}_c`, width: 11 },
      ]),
      { header: '건강 합계', key: 'th', width: 12 },
      { header: '요양 합계', key: 'tc', width: 12 },
      { header: '총계', key: 'tt', width: 12 },
    ];

    ws.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
    ws.getRow(1).alignment = { horizontal: 'center', vertical: 'middle' };

    employees.forEach(emp => {
      const rowData = {
        name: emp.name, id_number: emp.id_number, hire_date: emp.hire_date,
        th: Math.round(emp.total_health), tc: Math.round(emp.total_care), tt: Math.round(emp.total),
      };
      months.forEach(m => {
        rowData[`${m}_h`] = Math.round(emp.monthly?.[String(m)]?.health || 0);
        rowData[`${m}_c`] = Math.round(emp.monthly?.[String(m)]?.care || 0);
      });
      ws.addRow(rowData);
    });

    // 합계 행
    const totalData = { name: '합계', th: Math.round(settlement.totals.health), tc: Math.round(settlement.totals.care), tt: Math.round(settlement.totals.grand) };
    months.forEach(m => {
      totalData[`${m}_h`] = Math.round(monthTotal(m, 'health'));
      totalData[`${m}_c`] = Math.round(monthTotal(m, 'care'));
    });
    const totalRow = ws.addRow(totalData);
    totalRow.font = { bold: true };
    totalRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } };

    ws.eachRow(row => {
      row.eachCell(cell => {
        cell.border = {
          top: { style: 'thin' }, left: { style: 'thin' },
          bottom: { style: 'thin' }, right: { style: 'thin' },
        };
        if (typeof cell.value === 'number') cell.numFmt = '#,##0';
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `건강보험연말정산_${settlement.company?.name || ''}_${year}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (companiesLoading) {
    return <div className="health-settlement"><LoadingSpinner message="데이터를 불러오는 중..." /></div>;
  }
  if (companiesError) {
    return <div className="health-settlement"><ErrorMessage message={companiesError} type="error" /></div>;
  }

  return (
    <div className="health-settlement">
      <div className="health-layout">
        <aside className="health-sidebar">
          <CompanySearch
            companies={companies || []}
            onCompanySelect={setSelectedCompany}
            selectedCompany={selectedCompany}
            onAddCompany={() => {}}
            onEditCompany={() => {}}
            users={users || []}
          />
        </aside>

        <main className="health-content">
          {!selectedCompany ? (
            <div className="health-guide">
              <h2>🏥 건강보험연말정산</h2>
              <p>왼쪽에서 회사를 선택하면 해당 연도 임금대장에서 직원별 월별 건강보험·요양보험 공제액을 자동 취합합니다.</p>
            </div>
          ) : (
            <div className="health-panel">
              <div className="health-header">
                <h2>건강보험연말정산 — {selectedCompany.name}</h2>
                <div className="health-controls">
                  <label>
                    연도:
                    <select value={year} onChange={e => setYear(parseInt(e.target.value, 10))}>
                      {yearOptions.map(y => <option key={y} value={y}>{y}년</option>)}
                    </select>
                  </label>
                  <button
                    className="health-excel-btn"
                    onClick={handleExcelDownload}
                    disabled={!settlement || employees.length === 0}
                  >
                    📊 엑셀 다운로드
                  </button>
                </div>
              </div>

              {settlementLoading ? (
                <LoadingSpinner message="취합 중..." />
              ) : settlementError ? (
                <ErrorMessage message={settlementError} type="error" />
              ) : !settlement || months.length === 0 ? (
                <div className="health-empty">
                  <p>{year}년 임금대장 데이터가 없습니다.</p>
                </div>
              ) : (
                <>
                  <div className="health-summary">
                    <span>대상 월: {months.length}개월 ({months.join(', ')}월)</span>
                    <span>직원 수: {employees.length}명</span>
                    {retired.length > 0 && <span>{year}년 퇴직자 {retired.length}명 제외</span>}
                    <span>건강 합계: {fmt(settlement.totals.health)}원</span>
                    <span>요양 합계: {fmt(settlement.totals.care)}원</span>
                    <span className="grand">총계: {fmt(settlement.totals.grand)}원</span>
                  </div>

                  <div className="health-table-wrapper">
                    <table className="health-table">
                      <thead>
                        <tr>
                          <th rowSpan={2} className="sticky-col">성명</th>
                          <th rowSpan={2}>주민번호</th>
                          {months.map(m => <th key={m} colSpan={2}>{m}월</th>)}
                          <th colSpan={3}>합계</th>
                        </tr>
                        <tr>
                          {months.map(m => (
                            <React.Fragment key={m}>
                              <th className="sub">건강</th>
                              <th className="sub">요양</th>
                            </React.Fragment>
                          ))}
                          <th className="sub">건강</th>
                          <th className="sub">요양</th>
                          <th className="sub">총계</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employees.map(emp => (
                          <tr key={emp.employee_id || emp.name + emp.id_number}>
                            <td className="sticky-col name-col">{emp.name}</td>
                            <td className="rrn-col">{emp.id_number || '(미입력)'}</td>
                            {months.map(m => (
                              <React.Fragment key={m}>
                                <td className="num">{fmt(emp.monthly?.[String(m)]?.health)}</td>
                                <td className="num">{fmt(emp.monthly?.[String(m)]?.care)}</td>
                              </React.Fragment>
                            ))}
                            <td className="num total">{fmt(emp.total_health)}</td>
                            <td className="num total">{fmt(emp.total_care)}</td>
                            <td className="num total grand">{fmt(emp.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td className="sticky-col" colSpan={2}>합계</td>
                          {months.map(m => (
                            <React.Fragment key={m}>
                              <td className="num">{fmt(monthTotal(m, 'health'))}</td>
                              <td className="num">{fmt(monthTotal(m, 'care'))}</td>
                            </React.Fragment>
                          ))}
                          <td className="num">{fmt(settlement.totals.health)}</td>
                          <td className="num">{fmt(settlement.totals.care)}</td>
                          <td className="num grand">{fmt(settlement.totals.grand)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {retired.length > 0 && (
                    <div className="health-retired">
                      <h3>{year}년 퇴직자 명단 <span>{retired.length}명 · 퇴직할 때 따로 정산하므로 위 결과와 합계에 넣지 않았습니다</span></h3>
                      <table className="health-table">
                        <thead>
                          <tr><th>성명</th><th>주민번호</th><th>입사일</th><th>퇴사일</th></tr>
                        </thead>
                        <tbody>
                          {retired.map(emp => (
                            <tr key={emp.employee_id || emp.name + emp.id_number}>
                              <td className="name-col">{emp.name}</td>
                              <td className="rrn-col">{emp.id_number || '(미입력)'}</td>
                              <td>{emp.hire_date || '-'}</td>
                              <td>{emp.leave_date}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default HealthInsuranceSettlement;
